import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input-gg';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ParkerService } from '../services/parker.service';
import { EMAIL_VALIDATION_PATTERN, MULTILINGUAL_NAME_VALIDATION_PATTERN } from '@shared/constant/shared.constant';
import { ToastService } from '@shared/services/toast.service';
import { SuperAdminService } from '@core/services/super-admin.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonFunctions } from '@shared/common-functions';
import { AddParkeUserRequest } from '@core/models/api-response.model';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { ConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-add-parker-modal',
  templateUrl: './add-parker-modal.component.html',
  styleUrls: ['./add-parker-modal.component.scss']
})
export class AddParkerModalComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  @ViewChild('dropdownRef') dropdownRef!: ElementRef;
  nationalNumber: PhoneNumberFormat;
  searchCountryField = SearchCountryField;
  countryISO: CountryISO = CountryISO.UnitedStates;
  phoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  separateDialCode: boolean = false;
  addParkerForm: FormGroup;
  groupUserId: string = '';
  accountErrorMsg: string = '';
  filteredemail: any[] = [];
  driverEmailIdReadOnly: false;
  filteredMobile: any[] = [];
  cleanedNumber: string = '';
  isLoading: boolean = false;
  resetNumber: boolean = false;
  requiredLastNameAsterisk: boolean = false;
  isEmailSearch: boolean = false;
  isEmailSelected: boolean = false;
  isMoblieSelected: boolean = false;
  phoneNumber : any =
  {
    number: '',
    dialCode: '',

  };

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly ref: DynamicDialogRef,
    private readonly parkerService: ParkerService,
    private readonly config: DynamicDialogConfig,
    private readonly toastService: ToastService,
    private readonly superAdminService: SuperAdminService,
    private readonly configService: ConfigService
  ) {

  }

  ngOnInit(): void {
    this.groupUserId = this.config?.data?.groupUserId;
    this.createParkerForm();
  }
  createParkerForm(): void {
    const { validators, required } = CommonFunctions.getLastNameValidators('driver', this.configService);
    this.requiredLastNameAsterisk = required;
    this.addParkerForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(50), CommonFunctions.noLeadingSpacesValidator()]],
      lastName: ['', [...validators,Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(50), CommonFunctions.noLeadingSpacesValidator()]],
      email: ['', [Validators.pattern(EMAIL_VALIDATION_PATTERN)]],

    });
  }

  onMobileTyping(event: any) {
    const input = event.data;
    this.phoneNumber = event.data;
    if (this.isMoblieSelected) {
      this.addParkerForm.patchValue({
      firstName: '',
      lastName: '',
      email: ''
      });
      ['firstName','email', 'lastName'].forEach(field => {
      this.addParkerForm.get(field)?.enable();
      });
    }
    
    this.searchByContactField('mobile', input.number);
  }

  searchByContactField(type: 'email' | 'mobile', event) {
    let searchEmailQuery: string = '';
    let searchMobileNumberQuary: string = '';

    if (type === 'mobile') {
      this.isLoading = true;
      searchMobileNumberQuary = event;
    } else {
      searchEmailQuery = event.query;
    }

    const req: any = {
      searchEmail: searchEmailQuery ?? '',
      searchMobileNumber: searchMobileNumberQuary ?? '',
      groupUserId: this.groupUserId
    };

    this.superAdminService.getPredictiveSearchForDriverByEmailOrMobile(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (res: any) => {
        if (res.length > 0) {
          const mappedResults = this.mapUserResponseList(res);
          if (type === 'mobile') {
            this.filteredMobile = mappedResults;
            this.isLoading = false;
             this.isEmailSearch = false;
          } else {
            this.filteredemail = mappedResults;
            this.isEmailSearch = true;
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        if (type === 'mobile') {
          this.isLoading = false;
          this.filteredMobile = [{ message: error?.error?.message }];
        } else {
          this.filteredemail = [{ message: error?.error?.message }]
        }
      }
    });
  }

  private mapUserResponseList(res: any[]): any[] {
    return res.map(({ firstName, lastName, email, id, mobileNumber, countryCode, message }) => ({
      firstName,
      lastName,
      email,
      id,
      mobileNumber,
      countryCode,
      message: message ?? ''
    }));
  }

onMobileSelectionChange(res: any) {
    if (!res.data?.message) {
       this.isMoblieSelected = true;
       this.phoneNumber.dialCode = res.data.countryCode
       this.phoneNumber.number = res.data.mobileNumber
      this.addParkerForm.patchValue({
        email: res.data.email,
        firstName: res.data.firstName,
        lastName: res.data.lastName

      });
      ['email', 'firstName', 'lastName'].forEach(field => {
        this.addParkerForm.get(field)?.disable();
      });
    }
    this.filteredMobile = [];
  }

  onEmailSelectionChange(res: any,) {
    this.isEmailSelected = true;
    this.addParkerForm.patchValue({
      email: res.email,
      firstName: res.firstName,
      lastName: res.lastName,
      phone: res.mobileNumber
    });

    if (!res?.message) {
      ['firstName', 'phone', 'lastName'].forEach(field => {
        this.addParkerForm.get(field)?.disable();
      });
    }
    this.filteredMobile = [res];
  }

  onEmailInputChange(event) {
    this.isMoblieSelected = false;
    
    if(!event.data && this.isEmailSelected){
      this.addParkerForm.patchValue({
        firstName: '',
        lastName: '',
        phone: ''
      });
      ['firstName','phone', 'lastName'].forEach(field => {
        this.addParkerForm.get(field)?.enable();
      });
       this.resetNumber = true;
    }
  }

  addParker(): void {
    if (!this.validateForm()) {
      return;
    }
    
    const formValue = this.addParkerForm.getRawValue();
    let request: AddParkeUserRequest = {
      groupUserId: this.groupUserId,
      email: formValue.email,
      name: formValue.firstName,
      lastName: formValue.lastName,
      mobileNumber: this.phoneNumber?.number,
      countryCode: this.phoneNumber?.dialCode,
      iso: this.phoneNumber?.iso || this.countryISO
    }

    this.parkerService.addGroupUsersDriver(request)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response) => {
        if (response) {
          this.handleResponse(response);
        }
      },
      error: (error) => {
        this.handleError(error);
      }
    })

  }

  private validateForm(): boolean {
    if (this.addParkerForm.invalid) {
      this.addParkerForm.markAllAsTouched();
      return false;
    }
    return true;
  }

  private handleResponse(response): void {
    const successMessage = response?.message;
    this.toastService.showToastMessage('success', 'Success', successMessage);
    const data = { status: true };
    this.closeDialog(data);
    
  }

  private handleError(error: any): void {
    this.accountErrorMsg = error?.error?.message;
    
  }

  closeDialog(status): void {
    this.ref.close(status);
  }

  onResetHandled() {
    this.resetNumber = false;
  }

  onFirstNameLastNameTyping(): void {
    this.isMoblieSelected = false;
    this.isEmailSelected = false;
  }
}
